<template>
  <div>
    <portal
      v-if="assessors.length && componentVisible"
      to="competition-edit-tertiary"
    >
      <ul class="list-reset flex">
        <li class="mr-6">
          <button
            class="block py-4 text-black no-underline outline-none focus:outline-none"
            :class="{
              'opacity-50 hover:opacity-100': show !== 'invite'
            }"
            @click="show = 'invite'"
          >
            Invite
          </button>
        </li>
        <li class="mr-6">
          <button
            class="block py-4 text-black no-underline transition-all transition-normal outline-none focus:outline-none"
            :class="{
              'opacity-50 hover:opacity-100': show !== 'assign'
            }"
            @click="show = 'assign'"
          >
            Assign Applications
          </button>
        </li>
      </ul>
    </portal>
    <template v-if="show === 'invite'">
      <template v-if="!assessors.length">
        <div
          class="text-center border-dashed border-grey-200 border-2 px-4 py-12 bg-white"
        >
          <h2>You currently have no assessors</h2>
          <p class="mb-8">
            Assessors need to be added to a competition before the can be
            assigned applications.
          </p>
          <button
            class="text-primary-800 uppercase text-sm font-bold hover:opacity-75 transition-all transition-normal"
            @click="showAddAssessor = true"
          >
            <icon
              icon="add-circle"
              class="icons-primary w-16 h-16 mx-auto block mb-3"
            />
            Add Assessors
          </button>
        </div>
      </template>
      <template v-else>
        <icon-link
          href="#"
          class="mr-6"
          icon="add-circle"
          @click.prevent="showAddAssessor = true"
        >
          Add Assessors
        </icon-link>
        <icon-link href="#" icon="user-circle" @click.prevent="show = 'assign'">
          Assign Applications to Assessors
        </icon-link>
        <ul class="mt-4 list-reset flex flex-wrap -mx-4">
          <li
            v-for="(assessor, index) in assessors"
            :key="index"
            class="w-full lg:w-1/3 lg:flex p-4"
          >
            <user-card :user="assessor">
              <template #card-header>
                <button
                  class="absolute mt-2 mr-2 pin-t pin-r hover:opacity-75 transition-all transition-normal"
                  @click="removeAssessorByIndex(index, 1)"
                >
                  <icon
                    icon="close-circle"
                    class="icons-negative w-6 h-6 block"
                  />
                  <span class="hidden">Delete</span>
                </button>
              </template>
            </user-card>
          </li>
        </ul>
      </template>
      <portal to="modals">
        <app-modal v-if="showAddAssessor" @close="showAddAssessor = false">
          <div class="w-full">
            <app-card
              class="is-borderless max-w-sm mx-auto overflow-hidden p-4"
            >
              <div class="mb-6">
                <label for="searchAssessor" class="font-bold block mb-4">
                  <icon
                    icon="search"
                    class="w-6 h-6 icons-primary mr-2 align-middle"
                  />
                  Search existing members
                </label>
                <input
                  id="searchAssessors"
                  ref="assessorSearch"
                  v-model="searchValue"
                  type="search"
                  placeholder="Search for a member by name or email"
                  class="py-4 px-3 bg-grey-200 focus:outline-none w-full rounded"
                  autofocus
                  autocomplete="off"
                  @keyup="search"
                  @search="searchResults = []"
                />
                <p
                  v-if="searchValue.length > 3 && !searchResults.length"
                  class="text-negative-600 text-xs italic mt-1"
                >
                  <small>Unable to find this user</small>
                </p>
              </div>
              <ul v-if="searchResults.length" class="list-reset">
                <li
                  v-for="(result, index) in searchResults"
                  :key="index"
                  class="flex justify-between py-3 px-2"
                  :class="{
                    'border-b': index !== searchResults.length - 1
                  }"
                >
                  <div class="mr-2">
                    <p>{{ result.name }}</p>
                    <p
                      v-if="result.company !== null && result.company.name"
                      class="text-grey-700"
                    >
                      <small>{{ result.company.name | sentencecase }}</small>
                    </p>
                  </div>
                  <button
                    v-if="alreadyExists(result.id)"
                    type="button"
                    class="bg-negative-100 uppercase text-xs px-3 py-1 font-bold text-negative-800 focus:outline-none hover:bg-negative-200 focus:bg-negative-200 transition-all transition-normal"
                    @click="removeAssessorByID(result.id)"
                  >
                    Remove
                  </button>
                  <button
                    v-else
                    type="button"
                    class="bg-primary-100 uppercase text-xs px-3 py-1 font-bold text-primary-800 focus:outline-none hover:bg-primary-200 focus:bg-primary-200 transition-all transition-normal"
                    @click="addAssessor(result)"
                  >
                    Add
                  </button>
                </li>
              </ul>
              <form v-else @submit.prevent="inviteAssessor">
                <h2 class="text-base mb-2">
                  <icon
                    icon="user-add"
                    class="w-6 h-6 icons-primary mr-2 align-middle"
                  />
                  {{ addUserFormTitle }}
                </h2>
                <auth-input
                  v-model="invite.name"
                  label="Name"
                  input-type="text"
                  :required="true"
                  placeholder="e.g. Joanne Smith"
                  autocomplete="off"
                />
                <auth-input
                  v-model="invite.email"
                  label="Email"
                  input-type="email"
                  :required="true"
                  placeholder="e.g. joanne.smith@domain.com"
                  autocomplete="off"
                />
                <p v-if="searchValue.length < 3" class="italic mb-4 text-xs">
                  A user will be invited to sign up to the funding portal where
                  they will then be able to view assessments as they become
                  available.
                </p>
                <div class="md:w-1/2 mt-6">
                  <app-button type="submit" :disabled="duplicateInvite">
                    Add Invite
                  </app-button>
                </div>
              </form>
            </app-card>
          </div>
        </app-modal>
      </portal>
    </template>
    <template v-else>
      <competition-applications-assessors :state="state" />
    </template>
  </div>
</template>

<script>
export default {
  name: "CompetitionEditAssessors",
  props: {
    fields: {
      type: Object,
      require: true,
      default: null
    }
  },
  data() {
    return {
      componentVisible: false,
      show: "invite",
      showAddAssessor: false,
      searchResults: [],
      searchValue: "",
      invite: {
        name: "",
        email: ""
      }
    };
  },
  computed: {
    assessors: {
      get() {
        return this.fields.assessors;
      },
      set() {
        return this.$emit("update:field", { assessors: this.assessors });
      }
    },
    addUserFormTitle() {
      return this.searchValue.length > 3
        ? "Would you like to invite this assessor?"
        : "Invite assessor";
    },
    duplicateInvite() {
      return this.assessors.find(
        assessor => assessor.email === this.invite.email.toLowerCase()
      );
    }
  },
  watch: {
    showAddAssessor() {
      return (this.searchResults = []);
    }
  },
  created() {
    this.assessors = this.assessors.map(assessor => {
      if (!assessor.hasOwnProperty("invite")) assessor.invite = false;
      // If assessor exists in state, use the email_verified_at from that object
      let stateAssessor = this.stateAssessor(assessor.id);
      assessor.email_verified_at =
        stateAssessor !== undefined ? stateAssessor.email_verified_at : null;
      return assessor;
    });
  },
  activated() {
    this.componentVisible = true;
  },
  deactivated() {
    this.componentVisible = false;
  },
  methods: {
    alreadyExists(id) {
      return this.assessors.find(assessor => assessor.id === id);
    },
    stateAssessor(id) {
      return this.state.assessors.find(currAssessor => currAssessor.id == id);
    },
    inviteAssessor() {
      const assessor = { ...this.invite };
      const invitee = {
        id: null,
        name: assessor.name,
        email: assessor.email
      };
      this.invite.name = "";
      this.invite.email = "";
      this.$refs.assessorSearch.focus();
      return this.addAssessor(invitee, true);
    },
    addAssessor(assessor, invite = false) {
      assessor.invite = invite;
      return this.assessors.push(assessor);
    },
    removeAssessorByIndex(index) {
      return this.assessors.splice(index, 1);
    },
    removeAssessorByID(id) {
      return this.assessors.splice(
        this.assessors.findIndex(assessor => assessor.id === id),
        1
      );
    },
    search(event) {
      const value = event.target.value;
      if (value.length < 3) return (this.searchResults = []);
      axios
        .post(route("api.search.users"), {
          s: value
        })
        .then(({ data }) => {
          this.searchResults = data;
        });
    }
  }
};
</script>
